import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { apiUrl } from '../../config/config';
import WaveChart from './WaveChart';
import TrendChart from './TrendChart';
import {
  PARAMS, BATTERIES, TREND_PARAMS, paramStatus, SEVERITY_RANK,
  subscribe, getBuffer, getLatest, getTrend,
  getBufferGen, getLatestSampleTime, getOldestSampleTime,
  isServerUp, getServerUpSince,
} from './telemetryStore';
import { evaluateAlerts, alertSignature, techAlert } from './alerts';
import { onAlarmConfigChange, getAlarmConfig } from './alarmConfig';
import { recordLocal, getLocalHistory, fetchHistory, fmtDuration, describeValue } from './alertHistory';
import { isDemo } from './telemetryStore';
import { isPatientMuted, setPatientMuted, onPatientMuteChange, isAlarmMuted, setAlarmMuted } from './patientMute';
import { isMuted, onMuteChange, armAudio } from './alertSound';
import {
  subscribeTelemetry, getTelemetry, getTelemetryError, refreshTelemetry,
  sendDeviceCommand, fetchDeviceCommands, CARD_COMMANDS, describeCommandState,
} from './deviceTelemetry';
import { findSeekTargetForTime } from './datasetLookup';

// Witals24 only: persist each card's alarm-condition tracker across page
// refreshes (see trackerRef below).
function trackerStorageKey(deviceId) { return `lm_tracker_${deviceId}`; }
function loadPersistedTracker(deviceId) {
  try {
    const raw = localStorage.getItem(trackerStorageKey(deviceId));
    return raw ? JSON.parse(raw) : {};
  } catch (e) { return {}; }
}
function savePersistedTracker(deviceId, tracker) {
  try { localStorage.setItem(trackerStorageKey(deviceId), JSON.stringify(tracker)); } catch (e) { /* storage blocked */ }
}

function CriticalAlertDataset({ patientId, since }) {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    let cancelled = false;
    findSeekTargetForTime(patientId, since).then((t) => { if (!cancelled) setTarget(t); });
    return () => { cancelled = true; };
  }, [patientId, since]);
  if (!patientId || !target) return null;
  const { databaseid, start, end, xValue } = target;
  // Whether THIS strip has been analysed into classified beats — not the
  // patient's overall reportStatus — decides analysed vs unanalysed. A
  // patient can be "analysed" overall while this particular recent strip
  // hasn't been classified yet; sending that case to verifier-chart just
  // shows an empty chart, so route by what actually exists for this strip.
  const exact = start != null;
  const base = `/${exact ? 'verifier-chart' : 'verifier-unanalysed'}/${patientId}/${databaseid}`;
  const href = exact ? `${base}/${start}/${end}/${xValue}` : base;
  return (
    <a className="lm-alert-dataset" href={href} target="_blank" rel="noopener noreferrer"
       title={exact ? 'Open the ECG dataset and jump straight to this event' : 'Open the ECG dataset to see the event'}
       onClick={(e) => e.stopPropagation()}>
      Jump to event
    </a>
  );
}

const fmtAgo = (ms) => {
  if (!ms) return '—';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return new Date(ms).toLocaleDateString([], { day: '2-digit', month: 'short' });
};

const HISTORY_RANGES = [
  { key: '1h', label: '1 h', ms: 3600 * 1000 },
  { key: '24h', label: '24 h', ms: 24 * 3600 * 1000 },
  { key: '7d', label: '7 days', ms: 7 * 24 * 3600 * 1000 },
];
const fmtWhen = (ms) => {
  const d = new Date(ms);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })} ${time}`;
};

// Sweep: how many seconds of ECG span the card width. This is what makes the
// trace LOOK fast or slow — a phone showing ~6 s across its screen scrolls 5x
// faster than a 30 s window, even though both play at exactly real time.
const WINDOW_OPTIONS_S = [6, 10, 30];
const WINDOW_DEFAULT_S = 10;
const WINDOW_KEY = 'lm.windowSec';
function loadWindowSec() {
  try {
    const v = parseInt(window.localStorage.getItem(WINDOW_KEY), 10);
    if (WINDOW_OPTIONS_S.indexOf(v) >= 0) return v;
  } catch (e) { /* storage blocked — fall through */ }
  return WINDOW_DEFAULT_S;
}

// Vertical zoom (display gain) per card. ECG amplitude varies a lot by body
// type — lean/obese patients read SMALL (magnify with 2x), infants read LARGE
// (shrink with 0.5x) — so each card carries its own gain. It scales only the
// DISPLAY, never the stored data, and is remembered per patient (per deviceId).
const ZOOM_OPTIONS = [0.5, 1, 2];
const ZOOM_DEFAULT = 1;
const zoomKey = (deviceId) => `lm.zoom.${deviceId}`;
function loadZoom(deviceId) {
  try {
    const v = parseFloat(window.localStorage.getItem(zoomKey(deviceId)));
    if (ZOOM_OPTIONS.indexOf(v) >= 0) return v;
  } catch (e) { /* storage blocked — fall through */ }
  return ZOOM_DEFAULT;
}

const STALE_MS = 6 * 1000;     // no ECG samples for this long => the waveform is stale
// No packet of ANY kind for this long => the phone is disconnected. Must be well
// over two idle heartbeats: the app sends one every 5-6 s, so the old 6 s limit
// made a device-off patient flip to "disconnected" and back on ordinary network
// jitter — and every flip sounded as a brand-new alert.
const OFFLINE_MS = 15 * 1000;
// A connection alert that already sounded stays quiet if it comes back, until the
// patient has streamed good ECG continuously for this long. Stops a dozing phone
// or a loose electrode (drop, brief return, drop…) from re-alarming every cycle.
const REARM_MS = 30 * 1000;

// Playout buffer (media-player style): accumulate PREROLL_MS of data, then play
// it out at a steady real-time speed while staying this far behind the live
// edge. Because the render clock never catches up to the newest sample, the
// trace never stalls between batches — it scrolls continuously.
//
// The lag is BOUNDED: a monitor must favour recency over smoothness. Inside
// [LAG_MIN, LAG_MAX] we play at exactly 1x; outside we nudge; beyond LAG_HARD
// (a burst after a network stall, a backgrounded tab, a slow frame) we jump
// straight back to PREROLL behind live instead of crawling there at +15%.
const PREROLL_MS = 2000;       // frames arrive ~1/s + 0.5 s server batching → 2 s is the safe floor
const LAG_MIN_MS = 1200;       // closer than this to live → ease off so we never stall
const LAG_MAX_MS = 3000;       // further behind than this → catch up briskly
const LAG_HARD_MS = 5000;      // further behind than this → resync immediately

const LINE_COLOR = '#3987e5';
const STALE_COLOR = '#6b7683';

const HERO = PARAMS.find(p => p.hero) || PARAMS[0];
const SECONDARY = PARAMS.filter(p => p !== HERO);

function fmt(v, digits) {
  if (v == null || isNaN(v)) return '--';
  return Number(v).toFixed(digits);
}

/** Small sensor-battery badge for a tile corner (hidden until the value arrives). */
function BattBadge({ spec, value, stale, tag, status }) {
  if (value == null || isNaN(value)) return null;
  // `status` comes from the ACTIVE battery alarm (so the badge turns amber/red
  // at the thresholds set in Alarm settings); the fixed 20 % / 10 % bands are
  // only the fallback.
  const s = stale ? 'stale' : (status || paramStatus(spec, value));
  const pct = Math.max(0, Math.min(100, value));
  return (
    <span className={`lm-batt lm-batt-${s}`} title={spec.label}>
      {tag ? <span className="lm-batt-tag">{tag}</span> : null}
      <span className="lm-batt-icon">
        <span className="lm-batt-fill" style={{ width: `${pct}%` }} />
      </span>
      {Math.round(pct)}%
    </span>
  );
}

const fmtSecs = (s) => (s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)} min` : `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`);

/**
 * Critical alarms of this patient that need an answer from whoever is watching:
 * each stays red until someone clicks Acknowledge; the server records who and
 * when (and how long after the alarm) — the rapid-response record.
 */
function CriticalAcks({ alarms, onAck }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  if (!alarms || !alarms.length) return null;
  const ack = (a) => {
    setBusyId(a.id); setError('');
    onAck(a)
      .then(() => setBusyId(null))
      .catch(e => { setError(e.message); setBusyId(null); });
  };
  return (
    <div className="lm-crit">
      {alarms.map(a => (
        <div key={a.id} className={`lm-crit-row${a.ack ? ' lm-crit-done' : ''}`}>
          <div className="lm-crit-line">
            <span className="lm-crit-label">{a.ack ? '✓' : '!!!'} {a.label}</span>
            <span className="lm-crit-when" title={new Date(a.onset).toLocaleString()}>
              {fmtWhen(a.onset)}{a.cleared ? '' : ' · ongoing'}
            </span>
            {a.ack ? (
              <span className="lm-crit-by">
                Acknowledged by {a.ack.by || 'staff'} at {fmtWhen(new Date(a.ack.at).getTime())} (after {fmtSecs(a.ack.responseSec || 0)})
              </span>
            ) : (
              <button type="button" className="lm-crit-btn" disabled={busyId === a.id}
                onClick={(e) => { e.stopPropagation(); ack(a); }}>
                {busyId === a.id ? 'Saving…' : 'Acknowledge'}
              </button>
            )}
          </div>
        </div>
      ))}
      {error && <div className="lm-crit-error" role="alert">{error}</div>}
    </div>
  );
}

/**
 * One patient card: 5 parameter values + 1 live waveform.
 * Chart updates go straight to canvas; React state only carries the vitals
 * (updated at batch cadence, ~2/sec) and the computed status.
 */
// Which connection alarm a silent ECG means: the app reports Bluetooth off,
// or that it is still trying to reconnect; anything else is DEVICE OFF.
const silenceAlarm = (v) => (v && v.btOff ? 'bt_off' : v && v.reconnecting ? 'reconnecting' : 'device_off');

function LiveCard({ patient, onStatusChange, onAlertsChange, onRemove, onLocationChange, connAcked, hideTelemetry, persistAlerts, critical, onAck }) {
  const deviceId = patient.deviceId;
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const lastPacketRef = useRef(0);      // last ANYTHING from the app (data or heartbeat)
  const lastWaveRef = useRef(0);        // last actual ECG samples
  const firstPacketRef = useRef(0);     // first packet of this page session (grace for device-off)
  const goodSinceRef = useRef(null);    // start of the current run of good ECG (samples + electrodes on)
  const soundedRef = useRef({});        // conn alert key -> already sounded since the last good run
  const episodesRef = useRef({});       // conn alert key -> { quiet } for the episode now active
  const vitalsRef = useRef({});         // latest vitals (for the watchdog)
  // alarm rule id -> when its condition began (alarm condition delay). On
  // Witals24, reseeded from localStorage so a refresh doesn't restart every
  // alarm's delay timer (and make a still-active alarm vanish for a while).
  const trackerRef = useRef(persistAlerts ? loadPersistedTracker(deviceId) : {});
  const [vitals, setVitals] = useState(() => (getLatest(deviceId) || {}).vitals || {});
  const [status, setStatus] = useState('stale');
  const [staleFor, setStaleFor] = useState(null);
  // Link state: 'live' | 'leadoff' (app alive, electrodes off) |
  // 'deviceoff' (app alive, BLE sensor gone) | 'offline' (nothing from app)
  const [link, setLink] = useState('offline');
  const [alerts, setAlerts] = useState([]);
  const [showTrends, setShowTrends] = useState(false);
  const [trendKeys, setTrendKeys] = useState(['hr', 'spo2']);
  const [tip, setTip] = useState(null); // hover tooltip {x, time, values}
  // This patient's own alarm sound (the top-bar button mutes everyone)
  const [soundOff, setSoundOff] = useState(() => isPatientMuted(deviceId));
  const [allMuted, setAllMuted] = useState(isMuted);
  const [, setMuteTick] = useState(0);   // re-render when a single alarm is muted / unmuted
  useEffect(() => {
    // Sound just came back for this patient (their bell, or the top-bar mute).
    // Whatever alarmed while it was off was never HEARD, so it must not count
    // as "already sounded": forget that, make the active connection alarms
    // audible again, and force the next batch to re-report them.
    const audibleAgain = () => {
      if (isPatientMuted(deviceId) || isMuted()) return;
      soundedRef.current = {};
      Object.keys(episodesRef.current).forEach(k => { episodesRef.current[k].quiet = false; soundedRef.current[k] = true; });
      alertSigRef.current = '\u0000';
    };
    const a = onPatientMuteChange(() => { setSoundOff(isPatientMuted(deviceId)); setMuteTick(t => t + 1); audibleAgain(); });
    const b = onMuteChange((m) => { setAllMuted(m); audibleAgain(); });
    return () => { a(); b(); };
  }, [deviceId]);
  const toggleSound = () => { armAudio(); setPatientMuted(deviceId, !isPatientMuted(deviceId)); };
  // Device telemetry + remote control (FCM commands reach the phone even when
  // the live stream is down — that is how a disconnection is fixed from here).
  const [showTel, setShowTel] = useState(false);
  const [tel, setTel] = useState(null);
  const [telErr, setTelErr] = useState(null);
  const [cmds, setCmds] = useState(null);           // null = not loaded yet
  const [cmdBusy, setCmdBusy] = useState('');
  const [cmdNote, setCmdNote] = useState(null);     // { ok, text }
  const patientIdRef = useRef(patient.patientId);
  patientIdRef.current = patient.patientId;

  const loadCmds = useCallback(() => {
    fetchDeviceCommands(deviceId, patientIdRef.current)
      .then(list => setCmds(list))
      .catch(() => setCmds(prev => prev || []));
  }, [deviceId]);

  useEffect(() => {
    if (!showTel) return undefined;
    const sync = () => { setTel(getTelemetry(deviceId, patientIdRef.current)); setTelErr(getTelemetryError()); };
    const off = subscribeTelemetry(sync);
    sync();
    loadCmds();
    const t = setInterval(loadCmds, 15000);
    return () => { off(); clearInterval(t); };
  }, [showTel, deviceId, loadCmds]);

  const runCommand = (def) => {
    if (cmdBusy) return;
    if (def.confirm && !window.confirm(def.confirm)) return;
    setCmdBusy(def.cmd);
    setCmdNote(null);
    sendDeviceCommand(deviceId, patientIdRef.current, def.cmd, def.params)
      .then(r => {
        setCmdBusy('');
        setCmdNote({ ok: r.delivery === 'pushed', text: `"${def.label}": ${describeCommandState(r)}.` });
        // The app acknowledges within seconds when it is reachable — follow it.
        [1500, 4000, 8000, 15000].forEach(ms => setTimeout(() => { loadCmds(); refreshTelemetry(); }, ms));
      })
      .catch(e => { setCmdBusy(''); setCmdNote({ ok: false, text: `"${def.label}" was not sent: ${e.message}` }); });
  };

  // One-click recovery offered ON the alarm: the right command for what is wrong.
  const recover = (cmd) => {
    const def = CARD_COMMANDS.find(c => c.cmd === cmd);
    if (!def) return;
    setShowTel(true);      // show the outcome (sent → done by the app) where they just clicked
    runCommand(def);
  };

  // Alarm history panel
  const [showAlerts, setShowAlerts] = useState(false);
  const [histRange, setHistRange] = useState('24h');
  const [hist, setHist] = useState({ state: 'idle', rows: [], truncated: false });
  // Ward / bed inline editor
  const [editLoc, setEditLoc] = useState(false);
  const [wardDraft, setWardDraft] = useState('');
  const [bedDraft, setBedDraft] = useState('');
  const openLoc = () => { setWardDraft(patient.ward || ''); setBedDraft(patient.bed || ''); setEditLoc(true); };
  const saveLoc = () => { if (onLocationChange) onLocationChange(patient, wardDraft, bedDraft); setEditLoc(false); };
  // Enter submits the form (native behaviour, works with every keyboard/IME);
  // Escape cancels.
  const onLocKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); setEditLoc(false); } };
  const trendCanvasRef = useRef(null);
  const statusRef = useRef('stale');
  const alertSigRef = useRef('');
  // Sweep window (seconds across the card) — read by the RAF loop via the ref.
  const [windowSec, setWindowSec] = useState(loadWindowSec);
  const windowMsRef = useRef(windowSec * 1000);
  const lagRef = useRef(null);            // current playout lag (ms), written by the RAF loop
  const [lagSec, setLagSec] = useState(null);
  const cycleWindow = useCallback(() => {
    setWindowSec(prev => {
      const next = WINDOW_OPTIONS_S[(WINDOW_OPTIONS_S.indexOf(prev) + 1) % WINDOW_OPTIONS_S.length];
      windowMsRef.current = next * 1000;
      try { window.localStorage.setItem(WINDOW_KEY, String(next)); } catch (e) { /* ignore */ }
      if (chartRef.current) chartRef.current.reset(); // re-autoscale for the new span
      return next;
    });
  }, []);

  // Vertical zoom (gain) — read by the RAF loop via the chart instance.
  const [zoom, setZoom] = useState(() => loadZoom(deviceId));
  const zoomRef = useRef(zoom);
  const cycleZoom = useCallback(() => {
    setZoom(prev => {
      const next = ZOOM_OPTIONS[(ZOOM_OPTIONS.indexOf(prev) + 1) % ZOOM_OPTIONS.length];
      zoomRef.current = next;
      if (chartRef.current) chartRef.current.zoom = next; // next frame picks it up
      try { window.localStorage.setItem(zoomKey(deviceId), String(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, [deviceId]);

  // Called from the subscription/watchdog (never during render); the refs keep
  // the parent notifications outside React's state-updater phase.
  const reportStatus = useCallback((s) => {
    if (statusRef.current === s) return;
    statusRef.current = s;
    setStatus(s);
    onStatusChange(deviceId, s);
  }, [deviceId, onStatusChange]);

  const reportAlerts = useCallback((rawList) => {
    // Decide ONCE per episode whether a connection alert may sound. `quiet`
    // alerts still show, blink and appear in the Connection-alerts list; they
    // just don't beep again.
    const activeConn = {};
    rawList.forEach(a => { if (a.conn) activeConn[a.key] = true; });
    Object.keys(episodesRef.current).forEach(k => { if (!activeConn[k]) delete episodesRef.current[k]; });
    const list = rawList.map(a => {
      if (!a.conn) return a;
      let ep = episodesRef.current[a.key];
      if (!ep) {
        ep = episodesRef.current[a.key] = { quiet: !!soundedRef.current[a.key] };
        soundedRef.current[a.key] = true;
      }
      return ep.quiet ? { ...a, quiet: true } : a;
    });
    const sig = alertSignature(list);
    if (alertSigRef.current === sig) return;
    alertSigRef.current = sig;
    setAlerts(list);
    recordLocal(deviceId, list, vitalsRef.current);   // session fallback for the Alerts panel
    onAlertsChange(deviceId, list);
  }, [deviceId, onAlertsChange]);

  // Damp status flapping so cards don't re-sort under the user's cursor:
  // getting WORSE escalates immediately (alarm latency matters); getting
  // BETTER must hold for 3 consecutive batches (~1.5s) before it's reported.
  const pendingRef = useRef({ status: null, count: 0 });
  const reportStatusDamped = useCallback((s) => {
    if (s === statusRef.current) { pendingRef.current = { status: null, count: 0 }; return; }
    if (SEVERITY_RANK[s] < SEVERITY_RANK[statusRef.current]) {
      pendingRef.current = { status: null, count: 0 };
      reportStatus(s);
      return;
    }
    if (pendingRef.current.status === s) pendingRef.current.count++;
    else pendingRef.current = { status: s, count: 1 };
    if (pendingRef.current.count >= 3) {
      pendingRef.current = { status: null, count: 0 };
      reportStatus(s);
    }
  }, [reportStatus]);

  useEffect(() => {
    const chart = new WaveChart(canvasRef.current);
    chart.zoom = zoomRef.current;   // apply the remembered per-patient gain
    chartRef.current = chart;
    const onResize = () => chart.resize();
    window.addEventListener('resize', onResize);

    // --- Smooth continuous rendering (playout buffer) ----------------------
    // Draw every animation frame (~60fps). A render clock plays through the
    // data's OWN timeline at steady real-time speed, held ~2.5s behind the live
    // edge. Data arrives in bursty 500ms batches, but since the clock always has
    // a few seconds of buffered samples ahead of it, the trace scrolls smoothly
    // and never does the plot/stop/plot stutter.
    const colorRef = { current: LINE_COLOR };
    let raf = 0;
    let edge = null;        // render clock, in data-time (ms); null = pre-rolling
    let lastTs = null;      // previous frame timestamp (client clock)
    let gen = getBufferGen(deviceId);

    const render = (ts) => {
      const buf = getBuffer(deviceId);
      const newest = getLatestSampleTime(deviceId);
      const oldest = getOldestSampleTime(deviceId);

      // Buffer was wiped (lead-on refresh) → re-buffer and restart autoscale.
      const g = getBufferGen(deviceId);
      if (g !== gen) { gen = g; edge = null; chart.reset(); }

      if (buf && buf.size && newest != null && oldest != null) {
        if (edge == null) {
          // Pre-roll: wait until ~2 s of data is buffered, then start that far behind live.
          if (newest - oldest >= PREROLL_MS) edge = newest - PREROLL_MS;
        } else {
          // REAL elapsed time. (The old 100 ms cap silently discarded time on
          // every slow frame and whenever the tab was backgrounded, so the
          // trace drifted seconds behind live and only crept back at +15%.)
          const dt = lastTs != null ? ts - lastTs : 16;
          const lag = newest - edge;
          if (dt > 1000 || lag > LAG_HARD_MS) {
            edge = newest - PREROLL_MS;              // stalled/hidden/burst → jump back to live
          } else {
            let speed = 1;
            if (lag < LAG_MIN_MS) speed = 0.85;      // too close to live → ease off
            else if (lag > LAG_MAX_MS) speed = 1.5;  // behind → catch up briskly
            edge += dt * speed;                      // otherwise exact real time = smooth scroll
            if (edge > newest) edge = newest;        // never draw past the data
          }
        }
        if (edge != null) {
          lagRef.current = newest - edge;
          chart.draw(buf, windowMsRef.current, edge, colorRef.current);
        }
      }
      lastTs = ts;
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    const unsub = subscribe(deviceId, (batch) => {
      // Stamp with THIS browser's clock at receipt. batch.t is the server's
      // clock; comparing it with Date.now() here made any browser/server clock
      // difference over 6 s show a streaming device as offline (or never stale).
      const rx = Date.now();
      lastPacketRef.current = rx;
      if (!firstPacketRef.current) firstPacketRef.current = rx;
      if (batch.wave && batch.wave.length) {
        lastWaveRef.current = rx;
        colorRef.current = LINE_COLOR;
        setLink('live');
      }
      vitalsRef.current = batch.vitals || {};
      setVitals(batch.vitals || {});
      setStaleFor(null);
      const now = Date.now();
      const leadOff = !!(batch.vitals && batch.vitals.leadOff);

      // DEVICE OFF: the app is talking (this batch is a heartbeat or vitals
      // only) but no ECG samples have arrived for STALE_MS and the electrodes
      // aren't reported off. Decide it HERE too, not only in the watchdog —
      // otherwise every heartbeat would clear the alert and the watchdog would
      // re-raise it a second later (flapping + a beep each time).
      const hasWave = !!(batch.wave && batch.wave.length);
      // Re-arm the connection-alert sounds only after sustained good ECG.
      if (leadOff) goodSinceRef.current = null;
      else if (hasWave) {
        if (goodSinceRef.current == null) goodSinceRef.current = rx;
        else if (rx - goodSinceRef.current >= REARM_MS) soundedRef.current = {};
      }
      const waveSilent = !hasWave &&
        rx - (lastWaveRef.current || firstPacketRef.current) > STALE_MS;
      if (waveSilent && !leadOff) {
        colorRef.current = STALE_COLOR;
        setLink('deviceoff');
        reportAlerts([techAlert(silenceAlarm(batch.vitals))].filter(Boolean));
        reportStatus('stale');
        return;
      }

      // Every limit, arrhythmia and lead-off condition comes from the alarm
      // configuration (priority, message, persistence delay). Card status =
      // the highest-priority active alarm; a LOW-priority alarm shows its tag
      // but leaves the card "normal" (IEC low priority = awareness only).
      const active = evaluateAlerts(batch.vitals, { tracker: trackerRef.current, now });
      let worst = 'normal';
      for (const a of active) {
        if (a.severity !== 'low' && SEVERITY_RANK[a.severity] < SEVERITY_RANK[worst]) worst = a.severity;
      }
      if (persistAlerts) savePersistedTracker(deviceId, trackerRef.current);
      reportAlerts(active);
      reportStatusDamped(worst);
    });

    // Stale watchdog — a real-time UI must show silence, not a frozen number.
    // Three distinct silences:
    //   offline    nothing from the app at all (phone network gone / app killed)
    //   leadoff    app alive + leadOff flagged (electrodes off) — NOT offline
    //   deviceoff  app alive but no ECG samples and no lead-off → BLE sensor gone
    const watchdog = setInterval(() => {
      const now = Date.now();
      const pkt = lastPacketRef.current;
      const wav = lastWaveRef.current;
      // 1 Hz readout of the playout lag (how far the drawn edge trails the
      // newest received sample) — makes any delay visible and attributable.
      setLagSec(lagRef.current != null ? Math.round(lagRef.current / 100) / 10 : null);
      if (!pkt || now - pkt > OFFLINE_MS) {
        goodSinceRef.current = null;
        if (pkt) setStaleFor(Math.round((now - pkt) / 1000));
        colorRef.current = STALE_COLOR;   // the RAF loop repaints the trace grey
        setLink('offline');
        // DISCONNECTED alarm — only for a device that WAS connected in this
        // page session (a card that never received anything just says "no
        // data"), and only while this browser's own link to the server is up
        // and settled: if the dashboard lost the server, every card falls
        // silent at once and the page shows one banner instead of N alarms.
        const viewerOk = isServerUp() && now - getServerUpSince() > STALE_MS + 2000;
        reportAlerts(pkt && viewerOk ? [techAlert('disconnected')].filter(Boolean) : []);
        reportStatus('stale');
      } else if (!wav || now - wav > STALE_MS) {
        goodSinceRef.current = null;
        if (vitalsRef.current && vitalsRef.current.leadOff) {
          setLink('leadoff'); // alerts/status keep flowing via the batch path
        } else {
          colorRef.current = STALE_COLOR;
          // ECG stopped. DEVICE OFF needs proof the app is still alive: a packet
          // (heartbeat) received AFTER the last ECG sample. Without one we cannot
          // yet tell "sensor off" from "phone gone" — show the silence, raise
          // nothing, and let OFFLINE_MS decide. (Otherwise every phone drop
          // alarmed twice: DEVICE OFF at 6 s, then DISCONNECTED.)
          const appAlive = pkt - (wav || firstPacketRef.current) > 1500;
          if (appAlive) {
            setLink('deviceoff');
            // last-known vitals stop alerting; the loss itself alerts
            // Newer apps say why the ECG stopped: Bluetooth off, or still
            // trying to reconnect. Otherwise it is DEVICE OFF.
            const silence = silenceAlarm(vitalsRef.current);
            reportAlerts(now - firstPacketRef.current > STALE_MS ? [techAlert(silence)].filter(Boolean) : []);
          } else {
            setLink('offline');
            setStaleFor(Math.round((now - pkt) / 1000));
            reportAlerts([]);
          }
          reportStatus('stale');
        }
      }
    }, 1000);

    return () => {
      cancelAnimationFrame(raf);
      unsub();
      clearInterval(watchdog);
      window.removeEventListener('resize', onResize);
    };
  }, [deviceId, reportStatus, reportStatusDamped, reportAlerts]);

  // Alarm history: the server's record, refreshed while the panel is open. If
  // the server can't be reached, fall back to what this session saw — labelled.
  useEffect(() => {
    if (!showAlerts) return undefined;
    let cancelled = false;
    const rangeMs = (HISTORY_RANGES.find(r => r.key === histRange) || HISTORY_RANGES[1]).ms;
    const showSessionLog = () => { if (!cancelled) setHist({ state: 'local', rows: getLocalHistory(deviceId, rangeMs), truncated: false }); };
    const load = () => {
      if (isDemo()) { showSessionLog(); return; }
      fetchHistory(deviceId, rangeMs)
        .then(r => { if (!cancelled) setHist({ state: 'server', rows: r.alerts, truncated: r.truncated }); })
        .catch(showSessionLog);
    };
    setHist(h => ({ state: h.state === 'idle' ? 'loading' : h.state, rows: h.rows, truncated: h.truncated }));
    load();
    const t = setInterval(load, isDemo() ? 2000 : 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [showAlerts, histRange, deviceId]);

  // Alarm settings were edited: forget running persistence timers so every
  // rule is judged afresh against its new threshold / delay.
  useEffect(() => onAlarmConfigChange(() => { trackerRef.current = {}; }), []);

  // 24h trends panel: draw on open/toggle, refresh each minute, hover tooltip.
  useEffect(() => {
    if (!showTrends) return;
    const canvas = trendCanvasRef.current;
    const chart = new TrendChart(canvas);
    const params = TREND_PARAMS.filter(p => trendKeys.includes(p.key));
    const redraw = (hoverIndex) => chart.draw(getTrend(deviceId), params, hoverIndex);
    redraw(null);
    const timer = setInterval(() => redraw(null), 60000);
    const onResize = () => { chart.resize(); redraw(null); };
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const res = chart.hover(e.clientX - rect.left);
      redraw(res ? res.index : null);
      setTip(res);
    };
    const onLeave = () => { redraw(null); setTip(null); };
    window.addEventListener('resize', onResize);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      setTip(null);
    };
  }, [showTrends, trendKeys, deviceId]);

  const toggleTrendKey = (key) => {
    setTrendKeys(prev => {
      if (prev.includes(key)) {
        return prev.length > 1 ? prev.filter(k => k !== key) : prev; // keep at least one
      }
      return [...prev, key];
    });
  };

  // History link → opens the verifier view in a new tab. Analysed (report
  // status >= 2) goes to the chart; otherwise the unanalysed view. Same
  // decision the patient list uses. Needs the patient _id and dataset id.
  const analysed = (patient.reportStatus || 0) >= 2;
  const historyUrl = patient.patientId
    ? `/${analysed ? 'verifier-chart' : 'verifier-unanalysed'}/${patient.patientId}` +
      (patient.lastDatasetId ? `/${patient.lastDatasetId}` : '')
    : null;
  const openHistory = () => { if (historyUrl) window.open(historyUrl, '_blank', 'noopener'); };

  // Double-clicking an alarm-history row jumps the verifier straight to the ECG
  // recording that was active when the alarm fired. The verifier selects a file
  // only on an EXACT file-start match ("YYYY-MM-DD HH:MM:SS"), so the raw alarm
  // second won't do — we fetch this patient's recording list and pick the file
  // whose start is the latest at-or-before the alarm time (the session that
  // contains it), then open the verifier at that file's exact start. Analysed
  // patients go to the chart, otherwise the unanalysed view.
  const openHistoryAtTime = (onsetMs) => {
    if (!patient.patientId || !onsetMs) return;
    const base = analysed ? 'verifier-chart' : 'verifier-unanalysed';
    // Open the tab synchronously (inside the double-click gesture) so it isn't
    // popup-blocked; point it at the resolved file once the list loads.
    const win = window.open('about:blank', '_blank');
    const go = (dbid) => {
      const url = `/${base}/${patient.patientId}/${String(dbid).replace(/ /g, '%20')}`;
      if (win) win.location = url; else window.open(url, '_blank', 'noopener');
    };
    // The alarm's own wall clock, formatted like the verifier's file ids
    // ("YYYY-MM-DD HH:MM:SS"). The recording timestamps are stored as wall-clock
    // digits (tagged +00:00 but really local), and the sidebar matches on the
    // raw string — so we compare wall clock to wall clock, never parsed Dates,
    // to avoid a timezone offset selecting the wrong file.
    const alarmStr = (() => {
      const d = new Date(onsetMs);
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
           + `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    })();
    axios.get(`${apiUrl}api/common/patinentmenutreeNew?userid=${patient.patientId}`)
      .then((res) => {
        const tree = (res && res.data) || {};
        const files = [];
        Object.keys(tree).forEach((dateKey) => {
          (tree[dateKey] || []).forEach((item) => {
            if (!item || !item.date) return;
            const iso = String(item.date);          // e.g. 2026-09-24T14:21:15+00:00
            const datePart = iso.split('T')[0];
            const timePart = (iso.split('T')[1] || '').split('+')[0].split('.')[0];
            if (!datePart || !timePart) return;
            files.push(`${datePart} ${timePart}`);   // naive wall-clock id
          });
        });
        if (!files.length) { go(alarmStr); return; }
        // Zero-padded → lexicographic order is chronological.
        files.sort();
        // The recording active at the alarm = latest file starting at-or-before it.
        let chosen = files[0];
        for (const f of files) { if (f <= alarmStr) chosen = f; else break; }
        go(chosen);
      })
      .catch(() => go(alarmStr));
  };

  const isStale = status === 'stale';
  const heroVal = vitals[HERO.key];
  // Connection alerts (lead off / device off / disconnected) stay visible on a
  // stale card and keep the border blinking until someone acknowledges them
  // from the page's Connection-alerts button.
  // A tile is coloured by the alarm currently active on its parameter, so the
  // numbers light up exactly when (and as severely as) the configured limits say.
  const tileStatus = (key) => {
    if (isStale || vitals[key] == null || isNaN(vitals[key])) return 'stale';
    let s = 'normal';
    alerts.forEach(a => {
      if (a.param !== key || a.severity === 'low') return;
      if (SEVERITY_RANK[a.severity] < SEVERITY_RANK[s]) s = a.severity;
    });
    return s;
  };
  // Battery badges follow the thresholds in Alarm settings — immediately. (The
  // ALARM waits out its persistence delay before it sounds; a badge that stayed
  // green for those 30 s at 14 % would simply be wrong.)
  const battStatus = (key) => {
    const v = vitals[key];
    if (v == null || isNaN(v)) return 'normal';
    let s = 'normal';
    getAlarmConfig().rules.forEach(r => {
      if (!r.enabled || r.kind !== 'limit' || r.param !== key) return;
      if (!(r.op === 'lt' ? v < r.value : v > r.value)) return;
      const lvl = r.priority === 'high' ? 'critical' : 'warning';   // a low-priority battery limit still shows amber
      if (SEVERITY_RANK[lvl] < SEVERITY_RANK[s]) s = lvl;
    });
    return s;
  };
  const connAlerts = alerts.filter(a => a.conn);
  const shownAlerts = isStale ? connAlerts : alerts;
  const connBlink = connAlerts.some(a => a.severity !== 'low') && !connAcked;
  const connWorst = connAlerts.some(a => a.severity === 'critical') ? 'critical' : 'warning';
  // An unacknowledged critical alarm keeps the card blinking red until answered.
  const critOpen = (critical || []).some(a => !a.ack);
  const blink = critOpen ? ' lm-conn-blink lm-conn-critical' : (connBlink ? ` lm-conn-blink lm-conn-${connWorst}` : '');

  return (
    <div
      id={`lm-card-${deviceId}`}
      className={`lm-card lm-${status}${blink}`}
    >
      <div className="lm-card-head">
        <div className="lm-ident">
          <div className="lm-name-row">
            <span className="lm-name">{patient.name || patient.deviceId}</span>
            {!editLoc && (
              <button
                type="button"
                className={`lm-loc${patient.ward || patient.bed ? '' : ' lm-loc-empty'}`}
                title="Ward and bed — click to edit"
                onClick={(e) => { e.stopPropagation(); openLoc(); }}
              >
                {patient.ward || patient.bed ? (
                  <React.Fragment>
                    {patient.ward ? <span><span className="lm-loc-k">Ward</span> {patient.ward}</span> : null}
                    {patient.bed ? <span><span className="lm-loc-k">Bed</span> {patient.bed}</span> : null}
                  </React.Fragment>
                ) : '+ Ward / Bed'}
              </button>
            )}
          </div>
          <div className="lm-devid">{patient.deviceId}</div>
        </div>
        <div className="lm-head-right">
          <BattBadge spec={BATTERIES.mobileBatt} value={vitals.mobileBatt} stale={isStale} tag="PH" status={battStatus('mobileBatt')} />
          <span className={`lm-pill lm-pill-${status}`}>
            {isStale
              ? (link === 'deviceoff'
                  ? (vitals.btOff ? 'bluetooth off' : vitals.reconnecting ? 'reconnecting' : 'device off')
                  : (staleFor ? `offline ${staleFor}s` : 'no data'))
              : (alerts.some(a => a.key === 'lead_off') ? 'lead off' : status)}
          </span>
          <button
            type="button"
            className={`lm-trend-btn lm-bell${soundOff ? ' lm-bell-off' : ''}`}
            aria-pressed={soundOff}
            aria-label={soundOff ? `Turn alarm sound on for ${patient.name || deviceId}` : `Mute alarm sound for ${patient.name || deviceId}`}
            title={allMuted
              ? 'All sound is muted from the top bar. This bell only controls this patient.'
              : (soundOff
                ? 'Alarm sound is OFF for this patient (alarms still show). Click to turn it on.'
                : 'Alarm sound is on for this patient. Click to mute only this patient.')}
            onClick={(e) => { e.stopPropagation(); toggleSound(); }}
          >
            {soundOff ? '🔕 Off' : '🔔'}
          </button>
          <button
            type="button"
            className={`lm-trend-btn${showTrends ? ' lm-open' : ''}`}
            title="24-hour parameter trends"
            onClick={() => setShowTrends(s => !s)}
          >
            24h {showTrends ? '▴' : '▾'}
          </button>
          {!hideTelemetry && (
            <button
              type="button"
              className={`lm-trend-btn${showTel ? ' lm-open' : ''}`}
              title="Device telemetry and remote control: Bluetooth, recording, batteries — and commands to reconnect"
              aria-expanded={showTel}
              onClick={(e) => { e.stopPropagation(); setShowTel(v => !v); }}
            >
              Telemetry {showTel ? '▴' : '▾'}
            </button>
          )}
          <button
            type="button"
            className={`lm-trend-btn${showAlerts ? ' lm-open' : ''}`}
            title="Alarm history for this patient: what alarmed, when, and for how long"
            aria-expanded={showAlerts}
            onClick={(e) => { e.stopPropagation(); setShowAlerts(v => !v); }}
          >
            Alerts {showAlerts ? '▴' : '▾'}
          </button>
          <button
            type="button"
            className="lm-trend-btn lm-history-btn"
            title={historyUrl
              ? `Open ${analysed ? 'analysed chart' : 'unanalysed'} history in a new tab`
              : 'No history — patient/dataset not registered'}
            disabled={!historyUrl}
            onClick={(e) => { e.stopPropagation(); openHistory(); }}
          >
            History ↗
          </button>
          <button
            type="button"
            className="lm-remove"
            title="Remove this patient from the board"
            aria-label={`Remove ${patient.name || patient.deviceId}`}
            onClick={(e) => { e.stopPropagation(); onRemove(patient); }}
          >
            ×
          </button>
        </div>
      </div>

      {editLoc && (
        <form
          className="lm-loc-edit"
          onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => { e.preventDefault(); saveLoc(); }}
        >
          <label className="lm-loc-field">
            <span className="lm-loc-k">Ward</span>
            <input
              className="lm-loc-input" value={wardDraft} maxLength={24} autoFocus
              placeholder="e.g. ICU-2"
              onChange={e => setWardDraft(e.target.value)} onKeyDown={onLocKey}
            />
          </label>
          <label className="lm-loc-field">
            <span className="lm-loc-k">Bed</span>
            <input
              className="lm-loc-input lm-loc-input-bed" value={bedDraft} maxLength={24}
              placeholder="e.g. 14"
              onChange={e => setBedDraft(e.target.value)} onKeyDown={onLocKey}
            />
          </label>
          <button type="submit" className="lm-loc-save">Save</button>
          <button type="button" className="lm-loc-cancel" onClick={() => setEditLoc(false)}>Cancel</button>
        </form>
      )}

      <CriticalAcks alarms={critical} onAck={onAck} />

      {shownAlerts.length > 0 && (
        <div className="lm-card-alerts">
          {soundOff && !allMuted && (
            <span className="lm-alert-tag lm-alert-soundoff" title="This patient's alarm sound is muted — the alarms below are silent">🔕 SOUND OFF</span>
          )}
          {shownAlerts.map(a => (
            (() => {
              const silencedHere = isAlarmMuted(deviceId, a.key);
              const silencedForAll = a.audible === false;
              const quietTag = silencedHere || silencedForAll;
              return (
                <React.Fragment key={a.key}>
                <button
                  type="button"
                  className={`lm-alert-tag lm-alert-btn lm-alert-${a.severity}${(a.conn && connAcked) || quietTag ? ' lm-alert-acked' : ''}`}
                  aria-pressed={silencedHere}
                  disabled={silencedForAll}
                  title={silencedForAll
                    ? `${a.label} is muted for everyone in Alarm settings`
                    : `${a.priority ? a.priority.toUpperCase() + ' priority' : ''}${a.text ? ' — ' + a.text : ''}. `
                      + (silencedHere
                        ? 'Muted for this patient until it clears. Click to unmute.'
                        : 'Click to mute ONLY this alarm for ONLY this patient (until it clears).')}
                  onClick={(e) => { e.stopPropagation(); armAudio(); setAlarmMuted(deviceId, a.key, !silencedHere); }}
                >
                  {quietTag ? <span className="lm-alert-mark">🔕</span> : (a.mark ? <span className="lm-alert-mark">{a.mark}</span> : null)}
                  {a.label}{a.conn && connAcked ? ' · ack' : ''}
                </button>
                {a.key !== 'device_off' && a.key !== 'disconnected' && a.key !== 'lead_off' && (
                  <CriticalAlertDataset patientId={patient.patientId} since={a.since} />
                )}
                </React.Fragment>
              );
            })()
          ))}
          {shownAlerts.some(a => a.key === 'device_off' || a.key === 'reconnecting') && (
            <button type="button" className="lm-recover" disabled={!!cmdBusy}
              title="Push a command to the phone: connect to the ECG device over Bluetooth"
              onClick={(e) => { e.stopPropagation(); recover('connect'); }}>
              {cmdBusy === 'connect' ? 'Sending…' : '↻ Reconnect device'}
            </button>
          )}
          {shownAlerts.some(a => a.key === 'disconnected') && (
            <button type="button" className="lm-recover" disabled={!!cmdBusy}
              title="Push a wake-up to the phone so the app reconnects and reports its status"
              onClick={(e) => { e.stopPropagation(); recover('poll'); }}>
              {cmdBusy === 'poll' ? 'Sending…' : '↻ Wake app'}
            </button>
          )}
        </div>
      )}

      <div className="lm-vitals">
        <div className={`lm-hero lm-v-${tileStatus(HERO.key)}`}>
          <BattBadge spec={BATTERIES.ecgBatt} value={vitals.ecgBatt} stale={isStale} tag="ECG" status={battStatus('ecgBatt')} />
          <div className="lm-v-label">
            {HERO.label}{vitals.hrEstimated ? <span className="lm-est"> est.</span> : null}
          </div>
          <div className="lm-v-value">
            {fmt(heroVal, HERO.digits)}
            <span className="lm-v-unit">{HERO.unit}</span>
          </div>
        </div>
        <div className="lm-tiles">
          {SECONDARY.map(p => (
            <div
              key={p.key}
              className={`lm-tile${p.key === 'spo2' ? ' lm-tile-wide' : ''} lm-v-${tileStatus(p.key)}`}
            >
              {p.key === 'spo2' && (
                <BattBadge spec={BATTERIES.spo2Batt} value={vitals.spo2Batt} stale={isStale} status={battStatus('spo2Batt')} />
              )}
              <div className="lm-v-label">{p.label}</div>
              <div className="lm-v-value">
                {fmt(vitals[p.key], p.digits)}
                <span className="lm-v-unit">{p.unit}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="lm-wave">
        <canvas ref={canvasRef} className="lm-canvas" />
        <button
          type="button"
          className="lm-wave-tag lm-wave-zoom"
          title="Vertical zoom (gain): magnify a small-amplitude trace or shrink a large one to fit. Cycles 0.5× / 1× / 2×. Changes the display only, not the data. Remembered per patient."
          onClick={(e) => { e.stopPropagation(); cycleZoom(); }}
        >
          zoom {zoom}×
        </button>
        <button
          type="button"
          className="lm-wave-tag"
          title="Sweep: seconds of ECG across the card. Click to change (6 / 10 / 30 s). The second figure is how far the drawn trace trails the newest sample received by this browser."
          onClick={(e) => { e.stopPropagation(); cycleWindow(); }}
        >
          live · {windowSec}s sweep{link === 'live' && lagSec != null ? ` · −${lagSec.toFixed(1)}s` : ''}
        </button>
      </div>

      {!hideTelemetry && showTel && (
        <div className="lm-tel">
          <div className="lm-tel-head">
            <span className="lm-hist-title">
              Device telemetry
              <span className="lm-hist-count">
                {telErr ? '' : (tel ? `phone last reported ${fmtAgo(tel.last_seen_ms)}` : 'no report from this phone yet')}
              </span>
            </span>
            <button type="button" className="lm-hist-range" onClick={() => { refreshTelemetry(); loadCmds(); }}>Refresh</button>
          </div>

          {telErr && <div className="lm-hist-note" role="alert">{telErr.message}</div>}

          {!telErr && (
            <dl className="lm-tel-grid">
              <div><dt>Bluetooth</dt><dd className={tel && tel.ble_connected === false ? 'lm-tel-bad' : ''}>{!tel || tel.ble_connected == null ? '—' : (tel.ble_connected ? 'connected' : 'NOT connected')}</dd></div>
              <div><dt>Electrodes</dt><dd className={tel && tel.device_attached === false ? 'lm-tel-bad' : ''}>{!tel || tel.device_attached == null ? '—' : (tel.device_attached ? 'attached' : 'OFF')}</dd></div>
              <div><dt>Recording</dt><dd>{!tel ? '—' : (Number(tel.recording_status) === 1 ? 'recording' : Number(tel.recording_status) === 3 ? 'uploading' : Number(tel.recording_status) === 0 ? 'not recording' : '—')}</dd></div>
              <div><dt>Mode</dt><dd>{!tel ? '—' : (tel.app_mode === 'remote' ? 'Remote monitoring' : tel.app_mode === 'standalone' ? 'Standalone' : '—')}</dd></div>
              <div><dt>ECG battery</dt><dd>{tel && tel.battery_pct != null && !(Number(tel.battery_pct) === 0 && Number(tel.recording_status) !== 1) ? `${tel.battery_pct}%` : '—'}</dd></div>
              <div><dt>Phone battery</dt><dd>{tel && tel.mobile_battery_pct != null ? `${tel.mobile_battery_pct}%` : '—'}</dd></div>
              <div><dt>ECG waiting on phone</dt><dd>{tel && tel.pending_ecg != null ? tel.pending_ecg : '—'}</dd></div>
              <div><dt>Last upload</dt><dd>{tel ? fmtAgo(tel.last_upload_ms) : '—'}</dd></div>
              <div><dt>Device serial</dt><dd>{(tel && tel.serial_no) || '—'}</dd></div>
            </dl>
          )}

          <div className="lm-tel-cmds">
            {CARD_COMMANDS.map(def => (
              <button
                key={def.cmd} type="button" className={`lm-tel-cmd${def.confirm ? ' lm-tel-cmd-warn' : ''}`}
                title={def.hint} disabled={!!cmdBusy || (telErr && telErr.status === 403)}
                onClick={() => runCommand(def)}
              >
                {cmdBusy === def.cmd ? 'Sending…' : def.label}
              </button>
            ))}
          </div>

          {cmdNote && <div className={`lm-tel-note${cmdNote.ok ? '' : ' lm-tel-note-bad'}`} role="status">{cmdNote.text}</div>}

          <div className="lm-tel-log">
            <div className="lm-tel-log-title">Recent commands</div>
            {cmds == null && <div className="lm-hist-empty">loading…</div>}
            {cmds && cmds.length === 0 && <div className="lm-hist-empty">No commands sent to this patient yet.</div>}
            {cmds && cmds.slice(0, 5).map(c => (
              <div key={c.cmd_id || c._id} className="lm-tel-log-row">
                <span className="lm-hist-when">{fmtAgo(c.requested_ms)}</span>
                <span className="lm-tel-log-cmd">{c.command}</span>
                <span className={`lm-tel-log-state${c.status === 'failed' ? ' lm-tel-bad' : (c.status === 'executed' ? ' lm-tel-good' : '')}`}>
                  {describeCommandState(c)}{c.detail && c.detail !== 'ok' ? ` — ${c.detail}` : ''}
                </span>
                <span className="lm-tel-log-by">{c.requestedBy || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAlerts && (
        <div className="lm-hist">
          <div className="lm-hist-head">
            <span className="lm-hist-title">
              Alarm history
              <span className="lm-hist-count">
                {hist.state === 'loading' ? 'loading…' : `${hist.rows.length}${hist.truncated ? '+' : ''} in the last ${(HISTORY_RANGES.find(r => r.key === histRange) || {}).label}`}
              </span>
            </span>
            <span className="lm-hist-ranges">
              {HISTORY_RANGES.map(r => (
                <button
                  key={r.key} type="button"
                  className={`lm-hist-range${histRange === r.key ? ' lm-on' : ''}`}
                  onClick={() => setHistRange(r.key)}
                >{r.label}</button>
              ))}
            </span>
          </div>
          {hist.state === 'local' && (
            <div className="lm-hist-note">
              {isDemo()
                ? 'Demo: showing what this page has seen since it opened.'
                : 'Server history unavailable — showing only what this page has seen since it opened. This is not the record.'}
            </div>
          )}
          <div className="lm-hist-list">
            {hist.rows.length === 0 && hist.state !== 'loading' && (
              <div className="lm-hist-empty">No alarms in this period.</div>
            )}
            {hist.rows.map(ep => (
              <div
                key={ep.id}
                className={`lm-hist-row lm-hist-row-link${ep.ongoing ? ' lm-hist-ongoing' : ''}`}
                onDoubleClick={() => openHistoryAtTime(ep.onset)}
                title="Double-click to open this patient's ECG at this alarm's time"
              >
                <span className="lm-hist-when" title={new Date(ep.onset).toLocaleString()}>{fmtWhen(ep.onset)}</span>
                <span className={`lm-alert-tag lm-alert-${ep.severity === 'critical' ? 'critical' : (ep.severity === 'low' ? 'low' : 'warning')} lm-hist-tag`}>
                  <span className="lm-alert-mark">{ep.priority === 'high' ? '!!!' : (ep.priority === 'low' ? '!' : '!!')}</span>{ep.label}
                </span>
                <span className="lm-hist-value">{describeValue(ep)}</span>
                <span className="lm-hist-dur" title={ep.cleared ? `Ended ${new Date(ep.cleared).toLocaleString()}${ep.closedBy ? ` (${ep.closedBy})` : ''}` : 'Still active'}>
                  {ep.ongoing ? `ongoing · ${fmtDuration(ep.durationSec)}` : fmtDuration(ep.durationSec)}
                </span>
                {ep.code !== 'device_off' && ep.code !== 'disconnected' && ep.code !== 'lead_off' && (
                  <CriticalAlertDataset patientId={patient.patientId} since={ep.onset} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showTrends && (
        <div className="lm-trends">
          <div className="lm-trend-chips">
            {TREND_PARAMS.map(p => {
              const on = trendKeys.includes(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  className={`lm-trend-chip${on ? ' lm-on' : ''}`}
                  style={on ? { color: p.color, borderColor: p.color } : undefined}
                  onClick={() => toggleTrendKey(p.key)}
                >
                  <span className="lm-trend-dot" style={{ background: p.color }} />
                  {p.label}
                  <span className="lm-trend-val">
                    {fmt(vitals[p.key], p.digits)} {p.unit}
                  </span>
                </button>
              );
            })}
            <span className="lm-trend-note">last 24h · each line on its own scale</span>
          </div>
          <div className="lm-trend-wrap">
            <canvas ref={trendCanvasRef} className="lm-canvas" />
            {tip && (
              <div
                className="lm-trend-tip"
                style={{ left: Math.min(Math.max(tip.x - 60, 0), 9999) }}
              >
                <div className="lm-trend-tip-time">
                  {new Date(tip.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                {TREND_PARAMS.filter(p => trendKeys.includes(p.key)).map(p => (
                  <div key={p.key}>
                    <span className="lm-trend-dot" style={{ background: p.color }} />
                    {p.label} {tip.values[p.key] != null ? fmt(tip.values[p.key], p.digits) : '--'} {p.unit}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// memo: a card only re-renders from its own subscription, never from siblings.
export default React.memo(LiveCard);
